import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { useProcurement } from '@/context/ProcurementContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Send,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Building2,
  Calendar,
  DollarSign,
  Package,
  FileText,
  Mail,
  Globe,
  Truck,
  MessageCircle,
  ChevronDown,
  Receipt
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { DebitNotesForm } from '@/components/debitnotes';

const POManagement = () => {
  const navigate = useNavigate();
  const { state, actions } = useProcurement();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pending-approval');
  const [showDebitNotesForm, setShowDebitNotesForm] = useState(false);
  const [selectedPOForDebitNote, setSelectedPOForDebitNote] = useState<any>(null);

  useEffect(() => {
    if (state.purchaseOrders.length === 0) {
      actions.fetchPurchaseOrders();
    }
  }, []);

  // Filter POs by status
  const getPOsByStatus = (status: string) => {
    const statusMap: { [key: string]: string[] } = {
      'pending-approval': ['CREATED', 'Created/New', 'NEEDS_REVISION', 'PENDING_APPROVAL'],
      'approved': ['APPROVED'],
      'completed': ['Completed'],
      'rejected-cancelled': ['REJECTED', 'Cancelled']
    };

    const statuses = statusMap[status] || [];
    return state.purchaseOrders.filter(po => {
      let matchesStatus = statuses.includes(po.status);
      
      // For sent-to-vendors, show POs that have been sent to vendors but not yet verified
      if (status === 'sent-to-vendors') {
        matchesStatus = po.sentToVendor === true && po.status !== 'FULLY_RECEIVED' && po.status !== 'PARTLY_RECEIVED';
      }
      
      const matchesSearch = 
        po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (state.vendors.find(v => v.id === po.vendorId)?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesStatus && matchesSearch;
    }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "CREATED":
        return "outline";
      case "APPROVED":
        return "default";
      case "REJECTED":
        return "destructive";
      case "NEEDS_REVISION":
        return "secondary";
      case "FULLY_RECEIVED":
        return "default";
      case "FULLY_REJECTED":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getVendorName = (vendorId: number) => {
    const vendor = state.vendors.find(v => v.id === vendorId);
    return vendor?.name || `Vendor ${vendorId}`;
  };

  const handleSendToVendor = async (poId: number, method: 'email' | 'whatsapp') => {
    const po = state.purchaseOrders.find(p => p.id === poId);
    if (!po) return;

    const vendor = state.vendors.find(v => v.id === po.vendorId);
    if (!vendor) return;

    try {
      if (method === 'whatsapp') {
        // Open WhatsApp with pre-filled message
        const message = `Hello ${vendor.name},%0A%0APlease find attached the Purchase Order ${po.poNumber} for your review.%0A%0AThank you for your business.%0A%0A${state.currentUser?.name || 'KKG Electricals'}`;
        const whatsappUrl = `https://wa.me/${vendor.contactDetails.phone || '1234567890'}?text=${message}`;
        window.open(whatsappUrl, '_blank');
        toast.success(`WhatsApp opened for ${vendor.name}`);
      } else {
        // Send via email
        const subject = `Purchase Order ${po.poNumber} - KKG Electricals`;
        const body = `Dear ${vendor.name},%0A%0APlease find attached the Purchase Order ${po.poNumber} for your review.%0A%0ATotal Amount: ₹${po.totalAmount.toLocaleString()}%0A%0APlease confirm receipt and provide your delivery timeline.%0A%0AThank you for your business.%0A%0ABest regards,%0A${state.currentUser?.name || 'KKG Electricals Team'}`;
        const emailUrl = `mailto:${vendor.contactDetails.email}?subject=${encodeURIComponent(subject)}&body=${body}`;
        window.open(emailUrl, '_blank');
        toast.success(`Email client opened for ${vendor.name}`);
      }

      // Update PO status to sent
      await actions.sendPOToVendor(poId, po.vendorId, method, vendor.contactDetails.email);
    } catch (error) {
      toast.error('Failed to send PO to vendor');
    }
  };

  const handleSendForApproval = async (poId: number) => {
    try {
      // Update PO status to pending approval
      await actions.updatePurchaseOrder(poId, {
        status: 'PENDING_APPROVAL',
        updatedAt: new Date().toISOString()
      });
      
      toast.success('Purchase Order sent for approval successfully');
    } catch (error) {
      console.error('Failed to send PO for approval:', error);
      toast.error('Failed to send PO for approval');
    }
  };

  const handleCreateDebitNotes = (po: any) => {
    setSelectedPOForDebitNote(po);
    setShowDebitNotesForm(true);
  };

  const handleSaveDebitNote = async (debitNoteData: any): Promise<boolean> => {
    try {
      // Save to local storage
      const existingDebitNotes = JSON.parse(localStorage.getItem('DEBIT_NOTES') || '[]');
      const newDebitNote = {
        ...debitNoteData,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      const updatedDebitNotes = [...existingDebitNotes, newDebitNote];
      localStorage.setItem('DEBIT_NOTES', JSON.stringify(updatedDebitNotes));

      toast.success('Debit note created successfully');
      setShowDebitNotesForm(false);
      setSelectedPOForDebitNote(null);
      return true;
    } catch (error) {
      console.error('Failed to create debit note:', error);
      toast.error('Failed to create debit note');
      return false;
    }
  };

  const handleCloseDebitNotesForm = () => {
    setShowDebitNotesForm(false);
    setSelectedPOForDebitNote(null);
  };

  const handleSendPOAndGenerateInvoices = async (poId: number) => {
    const po = state.purchaseOrders.find(p => p.id === poId);
    if (!po) {
      console.error('PO not found:', poId);
      return;
    }

    const vendor = state.vendors.find(v => v.id === po.vendorId);
    if (!vendor) {
      console.error('Vendor not found for PO:', po);
      return;
    }

    console.log('Starting Send & Generate Invoices for PO:', po.poNumber, 'Vendor:', vendor.name);

    try {
      // Show loading toast
      toast.loading('Sending PO to vendor and generating invoices...', { id: 'send-po' });

      // Update PO status to sent
      console.log('Sending PO to vendor...');
      await actions.sendPOToVendor(poId, po.vendorId, 'email', vendor.contactDetails.email);
      
      // Generate random invoices (single full or multiple partial)
      console.log('Generating vendor invoices...');
      await generateVendorInvoices(po);

      // Update loading toast to success
      toast.success(`PO sent to ${vendor.name} and invoices generated!`, { id: 'send-po' });
      console.log('Successfully completed Send & Generate Invoices');
    } catch (error) {
      console.error('Error sending PO and generating invoices:', error);
      toast.error('Failed to send PO and generate invoices', { id: 'send-po' });
    }
  };

  const generateVendorInvoices = async (po: any) => {
    const vendor = state.vendors.find(v => v.id === po.vendorId);
    if (!vendor) {
      console.error('Vendor not found for invoice generation:', po.vendorId);
      return;
    }

    console.log('Generating invoices for PO:', po.poNumber, 'Vendor:', vendor.name);

    // Randomly decide: single full invoice or multiple partial invoices
    const isSingleInvoice = Math.random() < 0.4; // 40% chance of single invoice
    console.log('Invoice type:', isSingleInvoice ? 'Single' : 'Multiple');

    if (isSingleInvoice) {
      // Generate single full invoice
      console.log('Generating single full invoice...');
      await generateSingleInvoice(po, vendor);
    } else {
      // Generate multiple partial invoices
      console.log('Generating multiple partial invoices...');
      await generateMultipleInvoices(po, vendor);
    }
  };

  const generateSingleInvoice = async (po: any, vendor: any) => {
    const invoiceData = {
      invoiceNumber: `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
      poId: po.id,
      poNumber: po.poNumber,
      vendorId: po.vendorId,
      vendorName: vendor.name,
      poAmount: po.totalAmount,
      amount: po.totalAmount, // Full amount
      subtotal: po.totalAmount * 0.9, // 90% of total
      taxAmount: po.totalAmount * 0.1, // 10% tax
      totalAmount: po.totalAmount,
      issueDate: new Date().toISOString().split('T')[0],
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      status: 'Pending',
      receivedStatus: 'Normal',
      paymentTerms: 'Net 30',
      poQuantity: po.items.reduce((sum: number, item: any) => sum + item.quantity, 0),
      invoicedQuantity: po.items.reduce((sum: number, item: any) => sum + item.quantity, 0),
      receivedQuantity: po.items.reduce((sum: number, item: any) => sum + item.quantity, 0),
      pendingQuantity: 0,
      items: po.items.map((item: any, index: number) => ({
        id: index + 1,
        skuId: item.skuId,
        skuCode: `SKU-${String(item.skuId).padStart(3, '0')}`,
        skuName: item.skuName || `Item ${item.skuId}`,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
        receivedQuantity: item.quantity,
        pendingQuantity: 0
      })),
      notes: `Complete invoice for ${po.poNumber}`,
      attachments: [`invoice_${po.poNumber}.pdf`],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log('Creating single invoice with data:', invoiceData);
    const success = await actions.createVendorInvoice(invoiceData);
    console.log('Single invoice creation result:', success);
  };

  const generateMultipleInvoices = async (po: any, vendor: any) => {
    const totalAmount = po.totalAmount;
    const totalQuantity = po.items.reduce((sum: number, item: any) => sum + item.quantity, 0);
    
    // Generate 2-4 partial invoices
    const numInvoices = Math.floor(Math.random() * 3) + 2; // 2-4 invoices
    const amounts = generatePartialAmounts(totalAmount, numInvoices);
    const quantities = generatePartialQuantities(totalQuantity, numInvoices);

    for (let i = 0; i < numInvoices; i++) {
      const invoiceAmount = amounts[i];
      const invoiceQuantity = quantities[i];
      
      const invoiceData = {
        invoiceNumber: `INV-${new Date().getFullYear()}-${String(Date.now() + i).slice(-6)}`,
        poId: po.id,
        poNumber: po.poNumber,
        vendorId: po.vendorId,
        vendorName: vendor.name,
        poAmount: totalAmount,
        amount: invoiceAmount,
        subtotal: invoiceAmount * 0.9,
        taxAmount: invoiceAmount * 0.1,
        totalAmount: invoiceAmount,
        issueDate: new Date(Date.now() + i * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Staggered dates
        invoiceDate: new Date(Date.now() + i * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dueDate: new Date(Date.now() + (30 + i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: i === 0 ? 'Pending' : 'Approved', // First invoice pending, others approved
        receivedStatus: i === numInvoices - 1 ? 'Normal' : 'Not Received',
        paymentTerms: 'Net 30',
        poQuantity: totalQuantity,
        invoicedQuantity: invoiceQuantity,
        receivedQuantity: invoiceQuantity,
        pendingQuantity: totalQuantity - quantities.slice(0, i + 1).reduce((sum, q) => sum + q, 0),
        items: po.items.map((item: any, index: number) => {
          const itemQuantity = Math.floor((item.quantity / totalQuantity) * invoiceQuantity);
          return {
            id: index + 1,
            skuId: item.skuId,
            skuCode: `SKU-${String(item.skuId).padStart(3, '0')}`,
            skuName: item.skuName || `Item ${item.skuId}`,
            quantity: itemQuantity,
            unitPrice: item.unitPrice,
            totalPrice: itemQuantity * item.unitPrice,
            receivedQuantity: itemQuantity,
            pendingQuantity: 0
          };
        }),
        notes: `Partial invoice ${i + 1} of ${numInvoices} for ${po.poNumber}`,
        attachments: [`invoice_${po.poNumber}_part${i + 1}.pdf`],
        createdAt: new Date(Date.now() + i * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() + i * 24 * 60 * 60 * 1000).toISOString()
      };

      console.log(`Creating partial invoice ${i + 1} with data:`, invoiceData);
      const success = await actions.createVendorInvoice(invoiceData);
      console.log(`Partial invoice ${i + 1} creation result:`, success);
    }
  };

  const generatePartialAmounts = (totalAmount: number, numInvoices: number): number[] => {
    const amounts: number[] = [];
    let remaining = totalAmount;
    
    for (let i = 0; i < numInvoices - 1; i++) {
      // Generate random amount between 20% and 40% of remaining
      const minAmount = remaining * 0.2;
      const maxAmount = remaining * 0.4;
      const amount = Math.floor(Math.random() * (maxAmount - minAmount) + minAmount);
      amounts.push(amount);
      remaining -= amount;
    }
    
    // Last invoice gets the remaining amount
    amounts.push(remaining);
    
    return amounts;
  };

  const generatePartialQuantities = (totalQuantity: number, numInvoices: number): number[] => {
    const quantities: number[] = [];
    let remaining = totalQuantity;
    
    for (let i = 0; i < numInvoices - 1; i++) {
      // Generate random quantity between 20% and 40% of remaining
      const minQuantity = Math.floor(remaining * 0.2);
      const maxQuantity = Math.floor(remaining * 0.4);
      const quantity = Math.floor(Math.random() * (maxQuantity - minQuantity) + minQuantity);
      quantities.push(quantity);
      remaining -= quantity;
    }
    
    // Last invoice gets the remaining quantity
    quantities.push(remaining);
    
    return quantities;
  };


  const renderPOCard = (po: any) => (
    <Card key={po.id} className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold">{po.poNumber}</h3>
              {po.status === 'APPROVED' ? (
                <span className="badge bg-success text-white px-2 py-1 rounded text-sm">
                  {po.status}
                </span>
              ) : (
                <Badge variant={getStatusVariant(po.status)}>
                  {po.status}
                </Badge>
              )}
              {po.sentToVendor && (
                <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                  <Mail className="h-3 w-3 mr-1" />
                  Sent to Vendor
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Vendor</p>
                  <p className="font-medium">{getVendorName(po.vendorId)}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="font-medium">{formatCurrency(po.totalAmount)}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">
                    {new Date(po.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Items</p>
                  <p className="font-medium">{po.items?.length || 0}</p>
                </div>
              </div>
            </div>

            {po.notes && (
              <div className="mt-2">
                <p className="text-sm text-muted-foreground">Notes:</p>
                <p className="text-sm">{po.notes}</p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/purchase-orders/approve/${po.id}`)}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              View Details
            </Button>
            
            {(po.status === 'CREATED' || po.status === 'PENDING_APPROVAL') && (
              <Button
                onClick={() => handleSendForApproval(po.id)}
                size="sm"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <CheckCircle className="h-4 w-4" />
                Send for Approval
              </Button>
            )}
            
            {po.status === 'APPROVED' && (
              <div className="flex flex-col gap-2">
                {po.sentToVendor ? (
                  <Button
                    disabled
                    size="sm"
                    className="flex items-center gap-2 bg-gray-400 text-white cursor-not-allowed"
                  >
                    <Truck className="h-4 w-4" />
                    Sent
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleSendPOAndGenerateInvoices(po.id)}
                    size="sm"
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Truck className="h-4 w-4" />
                    Send
                  </Button>
                )}
                
                {po.sentToVendor ? (
                  <Button
                    disabled
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 text-gray-400 border-gray-200 cursor-not-allowed"
                  >
                    <Send className="h-4 w-4" />
                    Sent to Vendor
                  </Button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <Send className="h-4 w-4" />
                        Send to Vendor
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        onClick={() => handleSendToVendor(po.id, 'email')}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Mail className="h-4 w-4 text-blue-600" />
                        Send via Email
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleSendToVendor(po.id, 'whatsapp')}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <MessageCircle className="h-4 w-4 text-green-600" />
                        Send via WhatsApp
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )}
            
            {(po.status === 'REJECTED' || po.status === 'FULLY_REJECTED') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCreateDebitNotes(po)}
                className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Receipt className="h-4 w-4" />
                Debit Notes
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const pendingApprovalPOs = getPOsByStatus('pending-approval');
  const approvedPOs = getPOsByStatus('approved');
  const sentToVendorsPOs = getPOsByStatus('sent-to-vendors');
  const completedPOs = getPOsByStatus('completed');
  const rejectedCancelledPOs = getPOsByStatus('rejected-cancelled');

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">PO Management</h1>
            <p className="text-muted-foreground">Manage purchase orders throughout their lifecycle</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await import('@/services/mockAPI').then(m => m.MockAPIService.resetPurchaseOrdersToMock());
                  if (res.success) {
                    await actions.fetchPurchaseOrders();
                    toast.success('Purchase Orders reset to mock data');
                  } else {
                    toast.error('Failed to reset POs');
                  }
                } catch (e) {
                  console.error(e);
                  toast.error('Failed to reset POs');
                }
              }}
            >
              Reset
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button onClick={() => navigate('/purchase-orders')}>
              <Package className="h-4 w-4 mr-2" />
              Create PO
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by PO number or vendor name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                More Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Status Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="pending-approval" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending Approval ({pendingApprovalPOs.length})
            </TabsTrigger>
            <TabsTrigger value="approved" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Approved ({approvedPOs.length})
            </TabsTrigger>
            <TabsTrigger value="sent-to-vendors" className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              Sent to Vendors ({sentToVendorsPOs.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Completed ({completedPOs.length})
            </TabsTrigger>
            <TabsTrigger value="rejected-cancelled" className="flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              Rejected/Cancelled ({rejectedCancelledPOs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending-approval" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Pending Approval</h2>
              <p className="text-sm text-muted-foreground">
                {pendingApprovalPOs.length} purchase orders awaiting approval
              </p>
            </div>
            {pendingApprovalPOs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No pending approvals</h3>
                  <p className="text-muted-foreground text-center">
                    All purchase orders are up to date
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingApprovalPOs.map(renderPOCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="approved" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Approved POs</h2>
              <p className="text-sm text-muted-foreground">
                {approvedPOs.length} purchase orders ready to send to vendors
              </p>
            </div>
            {approvedPOs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No approved POs</h3>
                  <p className="text-muted-foreground text-center">
                    Approved purchase orders will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {approvedPOs.map(renderPOCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sent-to-vendors" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Sent to Vendors</h2>
              <p className="text-sm text-muted-foreground">
                {sentToVendorsPOs.length} purchase orders sent to vendors
              </p>
            </div>
            {sentToVendorsPOs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Send className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No sent POs</h3>
                  <p className="text-muted-foreground text-center">
                    Purchase orders sent to vendors will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sentToVendorsPOs.map(renderPOCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Completed</h2>
              <p className="text-sm text-muted-foreground">
                {completedPOs.length} fully invoiced and closed purchase orders
              </p>
            </div>
            {completedPOs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No completed POs</h3>
                  <p className="text-muted-foreground text-center">
                    Completed purchase orders will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedPOs.map(renderPOCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="rejected-cancelled" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Rejected/Cancelled</h2>
              <p className="text-sm text-muted-foreground">
                {rejectedCancelledPOs.length} declined or cancelled purchase orders
              </p>
            </div>
            {rejectedCancelledPOs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <XCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No rejected/cancelled POs</h3>
                  <p className="text-muted-foreground text-center">
                    Rejected or cancelled purchase orders will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {rejectedCancelledPOs.map(renderPOCard)}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Debit Notes Form Modal */}
        {showDebitNotesForm && selectedPOForDebitNote && (
          <DebitNotesForm
            isOpen={showDebitNotesForm}
            onClose={handleCloseDebitNotesForm}
            onSave={handleSaveDebitNote}
            linkedPO={selectedPOForDebitNote}
            mode="po"
          />
        )}
      </div>
    </AppLayout>
  );
};

export default POManagement;
